import { FC, useCallback, useEffect, useState } from 'react';
import classNames from 'classnames';

import { SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { getPlatformLinkUsingRouter, getSignupRedirectLink, rebuildReactTooltip, useUserSettings } from '@bpm-web-app/utils';
import { useGetMeSubscription } from '@bpm-web-app/swr-hooks';
import { EventType, SignUpStep, trackEvent } from 'libs/utils/src/lib/trackEvent';
import { Account, AccountPageProps } from '../../account';
import { PageHeader } from '../../components/page-header/page-header';
import { Card } from '../card/card';
import { LibrarySwitch } from '../library-switch/library-switch';
import { featuresData, useLibraryData } from './use-library-data';
import { StaticContent } from './static-content/static-content';
import { BillingPeriodToggle } from '../billing-period-toggle';
import accountStyles from '../../account.module.css';
import styles from '../plans.module.css';
import { usePlans } from './use-plans';
import { useBillingPeriod } from './use-billing-period';

type Props = AccountPageProps;

export const AccountPlansSupreme: FC<Props> = ({ title }) => {
    const { packages } = usePlans('supreme');
    const { isAnonymous } = useUserSettings();
    const [period, setPeriod] = useBillingPeriod();
    const [toggleDisabled, setToggleDisabled] = useState(false);
    const { data: currentSubscription } = useGetMeSubscription('supreme');
    const { library, setLibrary, selected } = useLibraryData();

    const subscriptions = packages?.data
        .filter((d) => {
            if (!(d.library === library || d.library === SubscriptionPackage.LibraryEnum.All)) {
                return false;
            }
            if (period === '1') {
                if (d.trial_period === 12) {
                    return false;
                }
                if (d.period === 1) {
                    return true;
                }
                return false;
            }
            if (period === '12') {
                if (d.period === 12) {
                    return true;
                }
                if (d.trial_period === 12) {
                    return true;
                }
            }
            return false;
        })
        .sort((a, b) => Number(a.amount) - Number(b.amount));

    useEffect(() => {
        if (subscriptions && subscriptions.length === 0 && period === '1') {
            setPeriod('12');
            setToggleDisabled(true);
        } else if (subscriptions && subscriptions.length > 0 && period === '1') {
            setToggleDisabled(false);
        }
        rebuildReactTooltip();
    }, [subscriptions, period, setPeriod, setToggleDisabled]);

    useEffect(() => {
        trackEvent({
            type: EventType.signUpStep,
            data: {
                platform: SubscriptionPackage.PlatformEnum.Supreme,
                step: SignUpStep.plansPage
            }
        });
    }, []);

    const getFormattedFeatures = useCallback((subscriptionPackage: SubscriptionPackage) => {
        const key = subscriptionPackage.library === SubscriptionPackage.LibraryEnum.All ? 'all' : subscriptionPackage.name.toString().toLowerCase();
        const featuresList = featuresData.supreme[key as keyof typeof featuresData.supreme].filter((feature) => {
            if (subscriptionPackage.included_product_ids?.length) {
                return true;
            }
            return typeof feature === 'string' || (typeof feature !== 'string' && !(feature as any).is_extra);
        });
        if (subscriptionPackage.library === SubscriptionPackage.LibraryEnum.All && library === SubscriptionPackage.LibraryEnum.Latin) {
            return featuresList.map((feature) => {
                if (typeof feature !== 'string') {
                    return { ...feature, label: feature.label.replace('Latino + Global', 'Open Format') };
                }
                return feature.replace('Latino + Global', 'Open Format');
            });
        }
        return featuresList;
    }, [
        library
    ]);

    return (
        <Account title={title} fluid>
            <div className={classNames(accountStyles['account__layout'], accountStyles['account__layout--wide'])}>
                <PageHeader title={title} hasBackButton={!isAnonymous} />
                <LibrarySwitch value={library} onChange={setLibrary} />

                <div className={styles['plans__header']}>
                    <p className={styles['plans__description']}>{selected?.description}</p>
                    <BillingPeriodToggle
                        value={period}
                        onChange={setPeriod}
                        disabled={toggleDisabled || !packages?.data.filter((s) => (s.period === 12 || s.trial_period === 12) && s.info?.allowed !== false).length}
                    />
                </div>

                <div className={styles['plans__cards']}>
                    {subscriptions?.map((subscription, i, s) => {
                        const cardLink = isAnonymous
                            ? getSignupRedirectLink(`/account/plan/change/${subscription.id}?backRoute=${encodeURIComponent(getPlatformLinkUsingRouter('/account/overview'))}`)
                            : `./plan/change/${subscription.id}`;
                        const payNowPromo = subscription.info?.promoted && subscription.info.promoted_amount && subscription.info?.promoted_amount !== '0.00' ? `Pay $${subscription.info.promoted_amount} today` : '';

                        let { amount } = subscription;
                        let periodStr = period === '1' || subscription.period === 1 ? 'mo' : 'yr';
                        let subTitle: string | undefined;
                        let strikeThroughText: string | undefined;
                        let hidePeriodSlash = false;

                        if (subscription.info?.trial_allowed && subscription.trial_period && subscription.trial_amount) {
                            periodStr = `${subscription.trial_period === 12 ? 'One Year' : subscription.trial_period === 1 ? 'First Month' : `${subscription.trial_period} Months*`}`;
                            strikeThroughText = subscription.trial_period === 12 ? `$${Number(subscription.info.trial_discount)}/12 Months` : `$${Number(subscription.amount)}/${subscription.period} Month${subscription.period > 1 ? 's' : ''}`;
                            amount = subscription.trial_amount;
                            if (subscription.period === 12) {
                                subTitle = `*$${subscription.amount}/year thereafter`;
                            } else {
                                subTitle = `*$${subscription.amount}/mo thereafter`;
                            }
                        } else if (subscription.info?.promoted && subscription.info.promoted_amount) {
                            strikeThroughText = `$${Number(subscription.amount)}/${subscription.period} ${subscription.period === 1 ? 'Month' : 'Months'}`;
                            amount = subscription.info.promoted_amount;
                            periodStr = ' now';
                            hidePeriodSlash = true;
                            if (subscription.period === 12) {
                                subTitle = `*$${subscription.amount}/year thereafter`;
                            } else {
                                subTitle = `*$${subscription.amount}/mo thereafter`;
                            }
                        }

                        return (
                            <Card
                                subscriptionPackage={subscription}
                                currentMembership={currentSubscription?.data.membership}
                                {...subscription}
                                amount={amount}
                                strikeThroughText={strikeThroughText}
                                subTitle={subTitle}
                                key={subscription.id}
                                customLibrary={subscription.library === SubscriptionPackage.LibraryEnum.All ? (
                                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 4 }}><span>Both Libraries + BPM Create</span>
                                        <svg data-tip="Thousands of royalty-free samples, loops & more for your edits, remixes and tracks. A Premium Plus ($14.99/mo value) is included with every All-Access subscription." width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M7.75 12H9.25V7H7.75V12ZM8.49563 5.5C8.70688 5.5 8.88542 5.42854 9.03125 5.28563C9.17708 5.14271 9.25 4.96563 9.25 4.75438C9.25 4.54313 9.17854 4.36458 9.03563 4.21875C8.89271 4.07292 8.71562 4 8.50438 4C8.29313 4 8.11458 4.07146 7.96875 4.21437C7.82292 4.35729 7.75 4.53437 7.75 4.74562C7.75 4.95687 7.82146 5.13542 7.96438 5.28125C8.10729 5.42708 8.28438 5.5 8.49563 5.5ZM8.50583 16C7.40472 16 6.36806 15.7917 5.39583 15.375C4.42361 14.9583 3.57292 14.3854 2.84375 13.6562C2.11458 12.9271 1.54167 12.0767 1.125 11.105C0.708333 10.1333 0.5 9.09514 0.5 7.99042C0.5 6.88569 0.708333 5.85069 1.125 4.88542C1.54167 3.92014 2.11458 3.07292 2.84375 2.34375C3.57292 1.61458 4.42333 1.04167 5.395 0.625C6.36667 0.208333 7.40486 0 8.50958 0C9.61431 0 10.6493 0.208333 11.6146 0.625C12.5799 1.04167 13.4271 1.61458 14.1562 2.34375C14.8854 3.07292 15.4583 3.92167 15.875 4.89C16.2917 5.85847 16.5 6.89319 16.5 7.99417C16.5 9.09528 16.2917 10.1319 15.875 11.1042C15.4583 12.0764 14.8854 12.9271 14.1562 13.6562C13.4271 14.3854 12.5783 14.9583 11.61 15.375C10.6415 15.7917 9.60681 16 8.50583 16ZM8.5 14.5C10.3056 14.5 11.8403 13.8681 13.1042 12.6042C14.3681 11.3403 15 9.80556 15 8C15 6.19444 14.3681 4.65972 13.1042 3.39583C11.8403 2.13194 10.3056 1.5 8.5 1.5C6.69444 1.5 5.15972 2.13194 3.89583 3.39583C2.63194 4.65972 2 6.19444 2 8C2 9.80556 2.63194 11.3403 3.89583 12.6042C5.15972 13.8681 6.69444 14.5 8.5 14.5Z" fill="#4A4A4A" />
                                        </svg>
                                    </div>
                                ) : undefined}
                                library={selected?.title}
                                disabled={subscription.is_current}
                                period={periodStr}
                                hidePeriodSlash={hidePeriodSlash}
                                link={cardLink}
                                listTitle={i ? `Everything in ${s[i - 1].name.toString()}, plus…` : 'Get started with…'}
                                title={(subscription.display_title || subscription.name).toString()}
                                theme={(subscription.library === SubscriptionPackage.LibraryEnum.All ? 'all' : subscription.name).toString().toLowerCase()}
                                features={getFormattedFeatures(subscription)}
                                tag={(subscription.is_promotion) ? subscription.promotion_text : payNowPromo}
                            />
                        );
                    }
                    )}
                </div>
            </div>

            <StaticContent
                onSelectPlan={() => {
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
            />
        </Account>
    );
};
